import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FilterCheckboxesComponent } from '../../../../Common/Filter/components/filterCheckboxes/filter-checkboxes.component';
import { FilterOptionModel } from '../../../../Common/Filter/models/filter-option.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';

import { FilterTypeTagCheckboxModel } from './filter-type-tag-checkbox.model';


@Component({
    selector: 'agpr-tag-filter-checkboxes',
    templateUrl: './tag-filter-checkboxes.component.html',
    styleUrls: [
        '../../../../Common/Filter/components/filterCheckboxes/filter-checkboxes.component.scss',
        './tag-filter-checkboxes.component.scss'
    ],
    standalone: false
})
export class TagFilterCheckboxesComponent extends FilterCheckboxesComponent implements OnInit, OnDestroy {
    @Input() filter: FilterTypeTagCheckboxModel;

    moreOptions: FilterOptionModel[] = [];
    filteredMoreOptions: FilterOptionModel[] = [];
    showAllFilters: boolean = false;

    subscriptions: Subscription[] = [];

    tagFilterForm: FormGroup = new FormGroup({
        includeExclude: new FormControl(),
        searchBar: new FormControl(),
        excludeAll: new FormControl(),
        optionForm: new FormGroup({}),
        moreOptionForm: new FormGroup({})
    });

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public tagsService: TagsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.refresh();
        this.subscriptions.push(
            this.tagsService.all$.subscribe(tags => {
                const filterOptionNames = this.filter.options.map(option => option.label);
                this.moreOptions = tags.filter(tag => filterOptionNames.indexOf(tag.name) === -1)
                    .map(tag => {
                        return { label: tag.name, value: tag.id } as FilterOptionModel<number>;
                    });
                this.filter.setMoreOptions(this.moreOptions);
                this.setUpmoreOptionsFormControl();
                this.filteredMoreOptions = this.moreOptions;
            }),
            this.tagFilterForm.get('searchBar').valueChanges.subscribe(searchQuery => {
                this.searchQuery = searchQuery;
                this.search();
            }),
            this.tagFilterForm.get('includeExclude').valueChanges.subscribe(includeExclude => {
                this.filter.setCurrentIncludeExclude(includeExclude);
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setUpFormControl(): void {
        this.filter.options.forEach(option => {
            this.optionFormGroup.addControl('option_' + option.value, new FormControl<boolean>(this.value.indexOf(option.value) > -1));
        });
        this.filter.options.forEach(option => {
            this.subscriptions.push(
                this.optionFormGroup.get('option_' + option.value).valueChanges.subscribe(optionValue => {
                    this.updateValue(option);
                })
            );
        });
        this.tagFilterForm.get('includeExclude').setValue(this.filter.includeExclude);
    }

    setUpmoreOptionsFormControl(): void {
        this.moreOptions.forEach(option => {
            this.moreOptionFormGroup.addControl('moreOption_' + option.value, new FormControl<boolean>(this.value.indexOf(option.value) > -1));
        });
        this.moreOptions.forEach(option => {
            this.subscriptions.push(
                this.moreOptionFormGroup.get('moreOption_' + option.value).valueChanges.subscribe(optionValue => {
                    this.updateValue(option);
                })
            );
        });
    }

    toggleShowAllFilters(): void {
        this.showAllFilters = !this.showAllFilters;
    }

    selectItems(): void {
        if (!this.anyOptionsSelected) {
            this.value = this.filteredOptions.map(option => option.value);
            this.filteredOptions.forEach(option => {
                this.optionFormGroup.get('option_' + option.value).setValue(true, { emitEvent: false });
            });
            if (this.showAllFilters) {
                this.value = this.value.concat(this.filteredMoreOptions.map(tag => tag.value));
                this.filteredMoreOptions.forEach(tag => {
                    this.moreOptionFormGroup.get('moreOption_' + tag.value).setValue(true, { emitEvent: false });
                });
            }
        } else {
            this.filteredOptions.forEach(option => {
                const index = this.value.indexOf(option.value);
                if (index > -1) {
                    this.value.splice(index, 1);
                }
                this.optionFormGroup.get('option_' + option.value).setValue(false, { emitEvent: false });
            });
            if (this.showAllFilters) {
                this.filteredMoreOptions.forEach(option => {
                    const index = this.value.indexOf(option.value);
                    if (index > -1) {
                        this.value.splice(index, 1);
                    }
                    this.moreOptionFormGroup.get('moreOption_' + option.value).setValue(false, { emitEvent: false });
                });
            }
        }
        this.handleChange();
    }

    /**
     * Refreshes the current value of the filter
     */
    refresh(): void {
        this.search();
        this.value = this.filter.getCurrentValue();
        this.anyOptionsSelected = this.value.length > 0;

        if (this.filter.options.length > 0 && Object.keys(this.optionFormGroup.controls).length === 0) {
            this.setUpFormControl();
        }
        this.filter.options.forEach(option => {
            this.optionFormGroup.get('option_' + option.value)?.setValue(this.value.indexOf(option.value) > -1, { emitEvent: false });
        });
        this.moreOptions.forEach(option => {
            this.moreOptionFormGroup.get('moreOption_' + option.value)?.setValue(this.value.indexOf(option.value) > -1, { emitEvent: false });
        });
    }

    search(): void {
        super.search();
        if (!this.searchQuery) {
            this.filteredMoreOptions = this.moreOptions;
            return;
        }
        const queryLower = this.searchQuery.toLocaleLowerCase();
        this.filteredMoreOptions = this.moreOptions.filter(option => option.label.toLocaleLowerCase().includes(queryLower));
    }

    updateValue(option: FilterOptionModel<number>): void {
        const index = this.value.indexOf(option.value);
        if (index > -1) {
            this.value.splice(index, 1);
        } else {
            this.value.push(option.value);
        }
        this.handleChange();
    }

    get optionFormGroup() {
        return this.tagFilterForm.get('optionForm') as FormGroup;
    }

    get moreOptionFormGroup() {
        return this.tagFilterForm.get('moreOptionForm') as FormGroup;
    }
}
